import { useMemo, useState } from "react";
import { useDepartures } from "../hooks/useBusData";
import { getRouteHintsForStop } from "../utils/demoDepartures";
import { countRouteVehicles, getLiveEstimateForService } from "../utils/liveEta";

const ROUTE_COLOURS = {
  "1": "#003087", "2": "#003087", "5": "#003087", "6": "#003087",
  "21": "#003087", "21/21A": "#003087", "22": "#003087", "25C": "#003087",
  "34": "#185FA5", "34/34A": "#185FA5", "42": "#003087", "42/42C": "#003087", "42/42A": "#003087", "42C": "#003087",
  "5/5A": "#003087", "8/9": "#003087", "20/20A": "#003087", "23/24": "#003087", "35/35A": "#185FA5",
  "43/43A": "#185FA5", "16/16A": "#185FA5", "44": "#185FA5", "48": "#185FA5", "50/51": "#1D9E75", "61": "#1D9E75", "70/70A": "#2c6e16",
  "10/10S": "#2c6e16", "11/11A": "#2c6e16", "12/12A": "#2c6e16", "City": "#1D9E75", "X80": "#A32D2D",
};

const TYPE_CLASS = {
  Normal: "type-normal",
  School: "type-school",
  "Park & Ride": "type-parkride",
  Circular: "type-circular",
  Special: "type-special",
};

function minutesUntilEndOfToday() {
  const now = new Date();
  const end = new Date(now);
  end.setHours(23, 59, 59, 999);
  return Math.max(0, Math.floor((end.getTime() - now.getTime()) / 60000));
}

function formatNextLabel(item) {
  if (!item || typeof item.minutes !== "number") return "Scheduled";
  const mins = item.minutes;
  if (mins <= 1) return "Due";
  if (mins < 60) return `${mins} min`;
  if (mins < 120) {
    const remainder = mins - 60;
    return remainder ? `1 hr ${remainder} min` : "1 hr";
  }

  const endOfToday = minutesUntilEndOfToday();
  if (mins <= endOfToday) return `Later today ${item.time}`;
  if (mins <= endOfToday + 24 * 60) return `Tomorrow ${item.time}`;

  const days = Math.floor((mins - endOfToday) / (24 * 60)) + 1;
  return `${days} days ${item.time}`;
}

function formatClockList(times) {
  return times.slice(0, 3).map((item, index) => {
    if (index === 0 && item.minutes > minutesUntilEndOfToday()) return `Tomorrow ${item.time}`;
    return item.time;
  }).join(" · ");
}

function RouteBadge({ route }) {
  return (
    <div className="route-badge" style={{ background: ROUTE_COLOURS[route] || "#003087" }}>
      {route}
    </div>
  );
}

function TypePill({ type }) {
  const label = type || "Normal";
  return <span className={`type-pill ${TYPE_CLASS[label] || "type-normal"}`}>{label}</span>;
}

function NextTimes({ dep }) {
  const times = dep.nextTimes || [];
  const live = dep.liveEstimate || null;
  const liveRouteCount = dep.liveRouteCount || 0;

  if (!times.length && !live) {
    return <span className="timetable-only">Timetable only</span>;
  }

  const first = times[0];
  return (
    <div className="next-times">
      {live && (
        <div className="live-estimate-box">
          <span className="source-pill live">Live approx</span>
          <span className="next-time-main live-main">{live.label}</span>
          <span className="next-time-clock">{live.distance}{live.confidence === "low" ? " · low confidence" : ""}</span>
        </div>
      )}
      {!live && liveRouteCount > 0 && (
        <div className="live-unmatched-box">
          <span className="source-pill live muted">Live seen</span>
          <span className="next-time-main">No safe ETA</span>
          <span className="next-time-clock">{liveRouteCount} route vehicle{liveRouteCount === 1 ? "" : "s"} visible</span>
        </div>
      )}
      {first && (
        <div className="scheduled-estimate-box">
          <span className="source-pill scheduled">Timetable</span>
          <span className="next-time-main">{formatNextLabel(first)}</span>
          <span className="next-time-clock">{formatClockList(times)}</span>
        </div>
      )}
    </div>
  );
}

export default function DepartureBoard({ stop, vehicles = [], onBack }) {
  const { departures, loading, source, error, meta } = useDepartures(stop);
  const [showSchoolServices, setShowSchoolServices] = useState(false);
  const areaRouteHints = getRouteHintsForStop(stop).slice(0, 5);

  const visibleDepartures = useMemo(() => {
    const withLive = departures.map((dep) => ({
      ...dep,
      liveEstimate: getLiveEstimateForService(dep, stop, vehicles),
      liveRouteCount: countRouteVehicles(dep, vehicles),
    }));
    const filtered = showSchoolServices ? withLive : withLive.filter((dep) => dep.serviceType !== "School");
    return [...filtered].sort((a, b) => {
      const aMinutes = a.liveEstimate?.minutes ?? a.nextTimes?.[0]?.minutes ?? a.mins ?? 999999;
      const bMinutes = b.liveEstimate?.minutes ?? b.nextTimes?.[0]?.minutes ?? b.mins ?? 999999;
      if (aMinutes !== bMinutes) return aMinutes - bMinutes;
      return String(a.route).localeCompare(String(b.route), undefined, { numeric: true });
    });
  }, [departures, showSchoolServices, stop, vehicles]);

  const schoolCount = departures.filter((dep) => dep.serviceType === "School").length;

  if (!stop) return null;

  return (
    <div>
      <button onClick={onBack} className="back-btn">← Back to stops</button>

      <div className="stop-title-card">
        <div className="section-label compact">Stop</div>
        <div className="selected-stop-name">{stop.name}</div>
        <div className="selected-stop-atco">ATCO: {stop.id}</div>
      </div>

      <div className="accuracy-note">
        Stop locations are exact from NaPTAN. Services shown in the main board are matched from BODS timetable data by this ATCO stop code. Live vehicle positions are used for cautious approximate ETAs where a matching route vehicle is visible; timetable times remain separate.
      </div>

      <div className={`match-note ${source === "bods-timetable" ? "match-note--success" : ""}`}>
        {source === "bods-timetable"
          ? `Exact services matched from BODS timetable data${meta?.cached ? " · cached" : ""}.`
          : "Exact stop services are being checked. If none are shown, the app will only show nearby guidance below."}
        {error ? ` API note: ${error}` : ""}
        {meta?.mode ? ` Checked ${meta.datasetsChecked || 0} dataset(s), ${meta.xmlFilesContainingStop || 0} file(s) contained this ATCO stop, in ${Math.round((meta.durationMs || 0) / 1000)}s.` : ""}
      </div>

      <div className="board-toolbar">
        <div>
          <div className="board-toolbar-title">Exact stop services</div>
          <div className="board-toolbar-subtitle">Verified against this stop code only</div>
        </div>
        {schoolCount > 0 && (
          <button
            className={`school-toggle ${showSchoolServices ? "on" : ""}`}
            onClick={() => setShowSchoolServices((value) => !value)}
            type="button"
          >
            School services: {showSchoolServices ? "On" : "Off"}
          </button>
        )}
      </div>

      <div className="service-board-card">
        <div className="service-board-head">
          <span>Route</span><span>Destination</span><span>Operator</span><span>Live / timetable</span>
        </div>

        {loading && (
          <div className="board-empty">
            Checking BODS timetable data for this exact stop…<br />
            First lookup can take a few seconds while the operator timetable file is scanned for services and any available scheduled times.
          </div>
        )}

        {!loading && departures.length > 0 && visibleDepartures.length === 0 && (
          <div className="board-empty">
            School services are currently hidden. Turn them back on to see the {schoolCount} school service{schoolCount === 1 ? "" : "s"} matched to this stop.
          </div>
        )}

        {!loading && departures.length === 0 && (
          <div className="board-empty">
            No exact BODS timetable services were matched to this stop yet.<br />
            This avoids showing buses that may not actually call here. Some BODS timetable files may not expose stop-level data in a simple format yet.
          </div>
        )}

        {!loading && visibleDepartures.map((dep, i) => (
          <div key={dep.key || i} className="service-board-row">
            <RouteBadge route={dep.route} />
            <div className="service-destination">{dep.dest}</div>
            <div className="service-operator">{dep.via || "Operator to confirm"}</div>
            <div className="service-status">
              <NextTimes dep={dep} />
              <TypePill type={dep.serviceType} />
              <span className="verified-label">Verified</span>
            </div>
          </div>
        ))}
      </div>

      <div className="nearby-guide-card">
        <div className="nearby-guide-head">
          <div>Nearby area route guide</div>
          <span>Not exact departures</span>
        </div>
        <div className="nearby-guide-subtitle">Useful local context only — not confirmed as serving this exact stop.</div>
        {areaRouteHints.map((service, i) => (
          <div key={`${service.route}-${i}`} className="nearby-route-row">
            <RouteBadge route={service.route} />
            <div>
              <div className="nearby-route-dest">{service.dest}</div>
              <div className="nearby-route-via">via {service.via}</div>
            </div>
          </div>
        ))}
      </div>

      <p className="data-credit">
        Stop locations from DfT NaPTAN · exact services from BODS timetable data · live approximate ETAs from BODS SIRI-VM vehicle positions
      </p>
    </div>
  );
}
